import React from 'react';

import { H2, H4 } from 'primitives/appTitle';
import AppSection from 'primitives/appSection';
import { AppInputTextFormik } from 'primitives/input/appInputText';
import { JoinSpecialtyLabels } from 'appConstants';
import LinkedInSvg from 'components/svg/icon/social/linkedInSvg';
import FiveHundredPXSvg from 'components/svg/icon/social/fiveHundredPXSvg';
import InstagramSvg from 'components/svg/icon/social/instagramSvg';
import BehanceSvg from 'components/svg/icon/social/behanceSvg';
import FacebookSvg from 'components/svg/icon/social/facebookSvg';
import { AppInputTextareaFormik } from 'primitives/input/appInputTextarea';

import { PhotographerApplicationDTO } from 'appAPITypes';
import { xor } from 'lodash';

import { FormikProps } from 'formik';
import { ShText } from '@shoootin/translations';
import { JoinPageTranslations } from '../joinPageTranslations';
import { getFrontMessage } from '../../../appMessageDefinitions';
import { ShCol, ShRow, ShTiles } from '@shoootin/components-web';
import { ShJoinSpecialties, ShJoinSpecialty } from '@shoootin/config';

export const PersonalInfos = ({
  form,
}: {
  form: FormikProps<PhotographerApplicationDTO>;
}) => {
  return (
    <AppSection
      className="left photograph-form"
      header={
        <H2>
          <ShText message={JoinPageTranslations.personalInfosTitle} />
        </H2>
      }
    >
      <ShRow className="field-row wrap-s">
        <ShCol xs={60} md={22}>
          <AppInputTextFormik
            form={form}
            name="userInfos.firstName"
            placeholderMessage={getFrontMessage(
              'form_userInfos_firstName_PlaceHolder',
            )}
            modifier="large"
            required
          />
        </ShCol>
        <ShCol xs={60} md={20}>
          <AppInputTextFormik
            form={form}
            name="userInfos.lastName"
            placeholderMessage={getFrontMessage(
              'form_userInfos_lastName_PlaceHolder',
            )}
            modifier="large"
            required
          />
        </ShCol>
        <ShCol xs={60} md={18}>
          <AppInputTextFormik
            form={form}
            name="userInfos.email"
            placeholderMessage={getFrontMessage(
              'form_userInfos_email_PlaceHolder',
            )}
            modifier="large"
            required
          />
        </ShCol>
      </ShRow>
      <ShRow className="field-row wrap-s">
        <ShCol xs={60} md={18}>
          <AppInputTextFormik
            form={form}
            name="userInfos.mobilePhone"
            placeholderMessage={getFrontMessage(
              'form_userInfos_mobilePhone_PlaceHolder',
            )}
            modifier="large"
            required
          />
        </ShCol>
        <ShCol xs={60} md={18}>
          <AppInputTextFormik
            form={form}
            name="userInfos.skype"
            placeholder="Skype"
            modifier="large"
          />
        </ShCol>
      </ShRow>

      <H4>
        <ShText message={JoinPageTranslations.companyInfosTitle} />
      </H4>
      <ShRow className="field-row wrap-s">
        <ShCol xs={60} md={20}>
          <AppInputTextFormik
            form={form}
            name="companyInfos.companyType"
            placeholderMessage={getFrontMessage(
              'form_prospectPhotographer_companyType_PlaceHolder',
            )}
            modifier="large"
          />
        </ShCol>
        <ShCol xs={60} md={20}>
          <AppInputTextFormik
            form={form}
            name="companyInfos.companyName"
            placeholderMessage={getFrontMessage(
              'form_prospectPhotographer_companyName_PlaceHolder',
            )}
            modifier="large"
          />
        </ShCol>
        <ShCol xs={60} md={20}>
          <AppInputTextFormik
            form={form}
            name="companyInfos.companyId"
            placeholderMessage={getFrontMessage(
              'form_prospectPhotographer_companyId_PlaceHolder',
            )}
            modifier="large"
          />
        </ShCol>
      </ShRow>

      <H4>
        <ShText message={JoinPageTranslations.specialityListTitle} />
      </H4>
      <ShTiles<ShJoinSpecialty>
        tiles={ShJoinSpecialties}
        activeTiles={form.values.specialityList}
        onTilePress={(key) =>
          form.setFieldValue(
            'specialityList',
            xor(form.values.specialityList, [key]),
          )
        }
        renderTileLabel={(key: ShJoinSpecialty) => (
          <ShText message={JoinSpecialtyLabels[key]} />
        )}
      />

      <ShRow className="field-row wrap-s">
        <ShCol xs={60} md={30}>
          <AppInputTextFormik
            form={form}
            name="social.web"
            placeholder="http://portfolio.com"
            modifier="large"
          />
        </ShCol>
        <ShCol xs={60} md={30}>
          <AppInputTextFormik
            form={form}
            name="social.facebook"
            placeholder="http://facebook.com"
            modifier="large"
            icon={<FacebookSvg />}
          />
        </ShCol>
      </ShRow>
      <ShRow className="field-row wrap-s">
        <ShCol xs={60} md={30}>
          <AppInputTextFormik
            form={form}
            name="social.behance"
            placeholder="http://behance.net"
            modifier="large"
            icon={<BehanceSvg />}
          />
        </ShCol>
        <ShCol xs={60} md={30}>
          <AppInputTextFormik
            form={form}
            name="social.instagram"
            placeholder="http://instagram.com"
            modifier="large"
            icon={<InstagramSvg />}
          />
        </ShCol>
      </ShRow>
      <ShRow className="field-row wrap-s">
        <ShCol xs={60} md={30}>
          <AppInputTextFormik
            form={form}
            name="social.fivehundredpx"
            placeholder="http://pageErrorpx.com"
            modifier="large"
            icon={<FiveHundredPXSvg />}
          />
        </ShCol>
        <ShCol xs={60} md={30}>
          <AppInputTextFormik
            form={form}
            name="social.linkedin"
            placeholder="http://linkedin.com"
            modifier="large"
            icon={<LinkedInSvg />}
          />
        </ShCol>
      </ShRow>

      <ShRow className="field-row gap wrap-s">
        <ShCol xs={60} md={30}>
          <AppInputTextareaFormik
            name="experiences"
            form={form}
            placeholderMessage={getFrontMessage(
              'form_prospectPhotographer_experience_PlaceHolder',
            )}
            modifier="large"
          />
        </ShCol>
        <ShCol xs={60} md={30}>
          <AppInputTextareaFormik
            name="about"
            form={form}
            placeholderMessage={getFrontMessage(
              'form_prospectPhotographer_about_PlaceHolder',
            )}
            modifier="large"
          />
        </ShCol>
      </ShRow>
    </AppSection>
  );
};
